import React from "react";
import { renderRichText } from "./richText";

// Shared, read-only markdown-style renderer used by panels that accept a single
// multiline `content` string (Clipboard-style). A line starting with:
//   "- " or "* "  -> bullet
//   "1. "         -> numbered
//   "[] " / "[x]" -> checkbox (read-only here; editing is done in the textarea)
// anything else   -> plain text. Links inside any line render as clickable.

export const MARKDOWN_HINT =
  "Tip: start a line with '- ' for a bullet, '1. ' for a numbered list, or '[] ' for a checkbox. Links become clickable.";

// Classify a single content line into a renderable kind.
export const parseMarkdownLine = (line) => {
  const todo = line.match(/^\[(x|X| )?\]\s?(.*)$/);
  if (todo) {
    return {
      type: "todo",
      checked: (todo[1] || "").toLowerCase() === "x",
      text: todo[2],
    };
  }
  const bullet = line.match(/^[-*]\s+(.*)$/);
  if (bullet) return { type: "bullet", text: bullet[1] };
  const number = line.match(/^(\d+)\.\s+(.*)$/);
  if (number) return { type: "number", number: number[1], text: number[2] };
  return { type: "text", text: line };
};

// Render a content string into rich, read-only markdown blocks.
// `keyPrefix` keeps React keys unique when several renderers share a page.
export const renderMarkdownContent = (content, keyPrefix = "md") => {
  if (!content || !content.trim()) {
    return <span className="text-gray-500 italic text-sm">No content</span>;
  }
  const lines = content.split("\n");
  return (
    <div className="space-y-1">
      {lines.map((line, i) => {
        const p = parseMarkdownLine(line);
        const key = `${keyPrefix}-${i}`;

        if (p.type === "todo") {
          return (
            <div key={key} className="flex gap-2 text-sm">
              <span className="flex-shrink-0 mt-0.5">
                {p.checked ? (
                  <span className="text-green-400">☑</span>
                ) : (
                  <span className="text-cyan-400">☐</span>
                )}
              </span>
              <span
                className={`whitespace-pre-wrap break-words ${
                  p.checked ? "text-gray-500 line-through" : "text-gray-300"
                }`}
              >
                {renderRichText(p.text, key)}
              </span>
            </div>
          );
        }
        if (p.type === "bullet") {
          return (
            <div key={key} className="flex gap-2 text-sm text-gray-300">
              <span className="text-cyan-400 flex-shrink-0">•</span>
              <span className="whitespace-pre-wrap break-words">
                {renderRichText(p.text, key)}
              </span>
            </div>
          );
        }
        if (p.type === "number") {
          return (
            <div key={key} className="flex gap-2 text-sm text-gray-300">
              <span className="text-cyan-400 flex-shrink-0">{p.number}.</span>
              <span className="whitespace-pre-wrap break-words">
                {renderRichText(p.text, key)}
              </span>
            </div>
          );
        }
        if (!p.text) {
          return <div key={key} className="h-2" />;
        }
        return (
          <p
            key={key}
            className="text-gray-300 text-sm whitespace-pre-wrap break-words"
          >
            {renderRichText(p.text, key)}
          </p>
        );
      })}
    </div>
  );
};
